/**
 * ForderungsmanagementPage — Mietwagen Haftpflichtfall
 * Module: Forderungsakten, Schadensminderungspflicht-Tagebuch, KI-Prüfassistent,
 *         Automatisches Anschreiben, Forderungsübersicht-Dashboard, Rentsoft Offene Posten
 * 
 * v2: Rentsoft-Integration für offene Posten und Rechnungsdaten
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useForderungen, type Forderungsakte, type SchadensminderungEintrag, type ForderungsMahnung } from '@/hooks/useForderungen';
import { useForderungenCrossLinks, type ForderungsCrossLinkMap } from '@/hooks/useForderungenCrossLinks';
import { useVerknuepfungenFilter } from '@/hooks/useVerknuepfungenFilter';
import { CrossLinkChips } from '@/components/forderungsmanager/CrossLinkChips';
import { matchesVerknuepfungenFilter, serializeVerknuepfungenFilter, type VerknuepfungenFilter } from '@/lib/verknuepfungen-filter';
import { useRentsoft, type RentsoftOpenItem, type RentsoftInvoice } from '@/hooks/useRentsoft';
import { useAuth } from '@/contexts/AuthContext';
import PlausibilitaetsBlock from '@/components/forderung/PlausibilitaetsBlock';
import DublettenWarnBanner from '@/components/forderung/DublettenWarnBanner';
import { findeDubletten } from '@/lib/dubletten';
import { useFallFilter } from '@/hooks/useFallFilter';
import FallFilterBar, { type FallFilterOption } from '@/components/filters/FallFilterBar';
import { matchesFallFilter } from '@/lib/fall-filter-url';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  downloadCsv,
  downloadXlsx,
  forderungExportColumns,
  type ForderungExportRow,
} from '@/lib/forderungen-export';
import { toast } from 'sonner';
import { useMobileBackHandler } from '@/hooks/useMobileBackHandler';
import { useGmail } from '@/hooks/useGmail';
import { supabase } from '@/lib/supabase';
import { buildVersicherungDraft } from '@/lib/bulk-versicherung-draft';
import {
  COLUMN_DEFS,
  loadColumnVisibility,
  saveColumnVisibility,
  sortForderungenDefault,
  type ColumnId,
} from '@/lib/forderungenColumns';
import {
  FileText, Plus, Search, AlertTriangle, CheckCircle, Clock,
  TrendingUp, Euro, Scale, BookOpen, Send, Download, Brain,
  ChevronRight, Calendar, User, Car, Shield, Gavel, Mail,
  BarChart3, PieChart, ArrowUpRight, AlertCircle, Pencil, Trash2, X, Eye,
  RefreshCw, ExternalLink, Receipt, Loader2, Bell, History,
  Phone, MapPin, Building2, Pen, ClipboardCheck, Hash, Link2, Columns3
} from 'lucide-react';

// ─── Status-Konfiguration ────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  offen: { label: 'Offen', color: 'bg-blue-100 text-blue-800', icon: Clock },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'bg-amber-100 text-amber-800', icon: FileText },
  abgerechnet: { label: 'Abgerechnet', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  strittig: { label: 'Strittig', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  gerichtlich: { label: 'Gerichtlich', color: 'bg-purple-100 text-purple-800', icon: Gavel },
  erledigt: { label: 'Erledigt', color: 'bg-gray-100 text-gray-600', icon: CheckCircle },
};

const MAHNSTUFE_CONFIG: Record<string, { label: string; color: string }> = {
  keine: { label: 'Keine Mahnung', color: 'bg-gray-100 text-gray-600' },
  '1_mahnung': { label: '1. Mahnung', color: 'bg-yellow-100 text-yellow-800' },
  '2_mahnung': { label: '2. Mahnung', color: 'bg-orange-100 text-orange-800' },
  inkasso: { label: 'Inkasso', color: 'bg-red-100 text-red-800' },
  anwalt: { label: 'Anwalt', color: 'bg-purple-100 text-purple-800' },
  gerichtlich: { label: 'Gerichtlich', color: 'bg-red-200 text-red-900' },
};

const RISIKO_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  stark: { label: 'Anspruch stark', color: 'text-green-700 bg-green-50 border-green-200', icon: Shield },
  mittel: { label: 'Anspruch mittel', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: AlertCircle },
  gefaehrdet: { label: 'Anspruch gefährdet', color: 'text-red-700 bg-red-50 border-red-200', icon: AlertTriangle },
};

const KATEGORIE_LABELS: Record<string, string> = {
  alternative_angeboten: 'Günstigere Alternative angeboten',
  reparaturdauer_ueberwacht: 'Reparaturdauer überwacht',
  kunde_informiert: 'Kunde über Pflichten informiert',
  versicherung_informiert: 'Versicherung informiert',
  verzoegerung: 'Verzögerung dokumentiert',
  sonstiges: 'Sonstiges',
};

const VERSICHERUNGEN = [
  'Allianz', 'HUK-COBURG', 'ADAC Versicherung', 'AXA', 'Zurich', 'Generali',
  'ERGO', 'HDI', 'VHV', 'Württembergische', 'Provinzial', 'R+V', 'Gothaer',
  'Debeka', 'LVM', 'Concordia', 'Itzehoer', 'Barmenia', 'Signal Iduna', 'Sonstige',
];

const FAHRZEUGKLASSEN = [
  'Kleinwagen (Gruppe A)', 'Kompaktklasse (Gruppe B)', 'Mittelklasse (Gruppe C)',
  'Obere Mittelklasse (Gruppe D)', 'Oberklasse (Gruppe E)', 'SUV (Gruppe F)',
  'Van/Kombi (Gruppe G)', 'Transporter (Gruppe H)', 'Cabrio/Sportwagen (Gruppe S)',
];

// ─── Hilfsfunktionen ─────────────────────────────────────
function formatCurrency(n: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}
function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE');
}
function daysUntil(d: string | null) {
  if (!d) return null;
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
}

// ═══════════════════════════════════════════════════════════
// HAUPTKOMPONENTE
// ═══════════════════════════════════════════════════════════
export default function ForderungsmanagementPage() {
  const {
    forderungen, loading, fetchForderungen,
    createForderung, updateForderung, deleteForderung,
    getEintraege, addEintrag, getMahnungen, addMahnung,
    getDokumente, addDokument, getStatistiken,
  } = useForderungen();

  const rentsoft = useRentsoft();
  const gmail = useGmail();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('uebersicht');
  const [selectedForderung, setSelectedForderung] = useState<Forderungsakte | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  // Mobile: Hardware-Back schließt Forderungs-Detail statt Seite zu verlassen
  useMobileBackHandler(!!selectedForderung, () => setSelectedForderung(null));
  const [fallFilter, setFallFilter] = useFallFilter();
  const [searchQuery, setSearchQuery] = useState('');

  // Spalten-Sichtbarkeit pro User persistent (localStorage, Issue #263)
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnId, boolean>>(
    () => loadColumnVisibility(user?.id ?? null),
  );
  useEffect(() => {
    setColumnVisibility(loadColumnVisibility(user?.id ?? null));
  }, [user?.id]);
  const setColumnVisible = useCallback((id: ColumnId, visible: boolean) => {
    setColumnVisibility(prev => {
      const next = { ...prev, [id]: visible };
      saveColumnVisibility(user?.id ?? null, next);
      return next;
    });
  }, [user?.id]);

  // Bulk-Aktion "Stellungnahme an Versicherung vorbereiten" (Issue #265):
  // ausgewählte Zeilen-IDs; bei Filter-/Tab-Wechsel absichtlich NICHT
  // zurückgesetzt, damit ein Benutzer über mehrere Filter sammeln kann.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDraftsBusy, setBulkDraftsBusy] = useState(false);

  // Cross-Link-Chips + URL-Filter (Issue #332)
  const { crossLinks } = useForderungenCrossLinks(forderungen);
  const { filter: verknFilter, setFilter: setVerknFilter } = useVerknuepfungenFilter();

  const stats = useMemo(() => getStatistiken(), [getStatistiken]);

  const statusOptions: FallFilterOption[] = useMemo(
    () => Object.entries(STATUS_CONFIG).map(([value, v]) => ({ value, label: v.label })),
    [],
  );

  const versicherungOptions: FallFilterOption[] = useMemo(() => {
    const aus = new Set<string>();
    forderungen.forEach(f => { if (f.versicherung_gegner) aus.add(f.versicherung_gegner); });
    VERSICHERUNGEN.forEach(v => aus.add(v));
    return Array.from(aus).sort((a, b) => a.localeCompare(b, 'de')).map(value => ({ value, label: value }));
  }, [forderungen]);

  const filtered = useMemo(() => {
    let list = forderungen.filter(f => matchesFallFilter(f, fallFilter));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(f =>
        f.akten_nr?.toLowerCase().includes(q) ||
        f.unfallgegner_name?.toLowerCase().includes(q) ||
        f.versicherung_gegner?.toLowerCase().includes(q) ||
        f.schaden_nummer?.toLowerCase().includes(q)
      );
    }
    if (verknFilter.hasAll || verknFilter.missing || verknFilter.demontage) {
      list = list.filter(f => {
        const cl = crossLinks[f.id] ?? { G: null, M: null, W: null };
        return matchesVerknuepfungenFilter(
          { G: !!cl.G, M: !!cl.M, W: !!cl.W, demontage_auslage: cl.demontage_auslage },
          verknFilter,
        );
      });
    }
    // Default-Sort greift beim ersten Render (Issue #263):
    //   Primär: Verjährung asc, Sekundär: Offener Betrag desc.
    return sortForderungenDefault(list);
  }, [forderungen, fallFilter, searchQuery, verknFilter, crossLinks]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Scale className="w-7 h-7 text-amber-600" />
            Forderungsmanagement Mietwagen
          </h1>
          <p className="text-muted-foreground mt-1">Haftpflichtfall — § 249 BGB Naturalrestitution</p>
        </div>
        <Button onClick={() => setShowNewForm(true)} className="gap-2">
          <Plus className="w-5 h-5" /> Neue Forderungsakte
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex overflow-x-auto whitespace-nowrap w-full max-w-4xl">
          <TabsTrigger value="uebersicht" className="gap-1.5"><BarChart3 className="w-4 h-4" /> Übersicht</TabsTrigger>
          <TabsTrigger value="akten" className="gap-1.5"><FileText className="w-4 h-4" /> Akten</TabsTrigger>
          <TabsTrigger value="offene_posten" className="gap-1.5"><Receipt className="w-4 h-4" /> Offene Posten</TabsTrigger>
          <TabsTrigger value="tagebuch" className="gap-1.5"><BookOpen className="w-4 h-4" /> Tagebuch</TabsTrigger>
          <TabsTrigger value="pruefung" className="gap-1.5"><Brain className="w-4 h-4" /> KI-Prüfung</TabsTrigger>
          <TabsTrigger value="anschreiben" className="gap-1.5"><Mail className="w-4 h-4" /> Anschreiben</TabsTrigger>
          <TabsTrigger value="mahnwesen" className="gap-1.5"><Bell className="w-4 h-4" /> Mahnwesen</TabsTrigger>
        </TabsList>

        {/* ═══ TAB: ÜBERSICHT (Modul 5) ═══ */}
        <TabsContent value="uebersicht" className="space-y-6">
          <DashboardOverview
            stats={stats}
            forderungen={forderungen}
            rentsoftOpenItems={rentsoft.openItems}
            rentsoftFinancials={rentsoft.financialStats}
            onSelect={(f) => { setSelectedForderung(f); setActiveTab('akten'); }}
          />
        </TabsContent>

        {/* ═══ TAB: AKTEN (Modul 1) ═══ */}
        <TabsContent value="akten" className="space-y-4">
          {/* Filter + Spaltenauswahl */}
          <div className="flex flex-col gap-3" data-testid="forderungen-filter-leiste">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative flex-1 w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Suche nach Akten-Nr, Gegner, Versicherung..." className="pl-9"
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </div>
              <VerknuepfungenFilterSelect value={verknFilter} onChange={setVerknFilter} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid="forderungen-column-picker"
                    aria-label="Spaltenauswahl"
                  >
                    <Columns3 className="w-4 h-4" /> Spalten
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Spalten ein-/ausblenden</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {COLUMN_DEFS.map(col => (
                    <DropdownMenuCheckboxItem
                      key={col.id}
                      checked={columnVisibility[col.id] ?? false}
                      disabled={col.alwaysVisible}
                      onCheckedChange={(checked) => setColumnVisible(col.id, Boolean(checked))}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`forderungen-column-toggle-${col.id}`}
                    >
                      {col.label}
                      {col.alwaysVisible && <span className="ml-2 text-xs text-muted-foreground">(Pflicht)</span>}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2 w-full sm:w-auto"
                    disabled={filtered.length === 0}
                    data-testid="forderungen-export-trigger"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="forderungen-export-csv"
                    onSelect={() => {
                      try {
                        downloadCsv(filtered as ForderungExportRow[], forderungExportColumns);
                        toast.success(`${filtered.length} Forderung(en) als CSV exportiert`);
                      } catch {
                        toast.error('CSV-Export fehlgeschlagen');
                      }
                    }}
                  >
                    Als CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="forderungen-export-xlsx"
                    onSelect={async () => {
                      try {
                        await downloadXlsx(filtered as ForderungExportRow[], forderungExportColumns);
                        toast.success(`${filtered.length} Forderung(en) als XLSX exportiert`);
                      } catch {
                        toast.error('XLSX-Export fehlgeschlagen');
                      }
                    }}
                  >
                    Als XLSX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <FallFilterBar
              state={fallFilter}
              onChange={setFallFilter}
              statusOptions={statusOptions}
              versicherungOptions={versicherungOptions}
              hide={{ businessUnit: true }}
            />
          </div>

          {/* Bulk-Aktion (Issue #265) */}
          <BulkDraftToolbar
            filtered={filtered}
            selectedIds={selectedIds}
            busy={bulkDraftsBusy}
            onToggleAll={(checked) => {
              if (checked) {
                const next = new Set(selectedIds);
                filtered.forEach(f => next.add(f.id));
                setSelectedIds(next);
              } else {
                const next = new Set(selectedIds);
                filtered.forEach(f => next.delete(f.id));
                setSelectedIds(next);
              }
            }}
            onBulkDraft={async () => {
              const rows = forderungen.filter(f => selectedIds.has(f.id));
              if (rows.length === 0) return;
              setBulkDraftsBusy(true);
              try {
                const { data: { session: authSession } } = await supabase.auth.getSession();
                const resp = await fetch('/api/forderung-batch-anschreiben', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(authSession?.access_token ? { Authorization: `Bearer ${authSession.access_token}` } : {}),
                  },
                  body: JSON.stringify({ forderungsakte_ids: rows.map(f => f.id) }),
                });
                const json = await resp.json();
                if (!resp.ok) throw new Error(json.error ?? `HTTP ${resp.status}`);
                const { summary } = json;
                const parts = [`${summary.success} Anschreiben erstellt`];
                if (summary.skipped > 0) parts.push(`${summary.skipped} übersprungen`);
                if (summary.failed > 0) parts.push(`${summary.failed} fehlgeschlagen`);
                const msg = parts.join(' · ');
                if (summary.failed > 0) toast.error(msg);
                else toast.success(msg);
              } catch (err: unknown) {
                toast.error(err instanceof Error ? err.message : 'Batch-Anschreiben fehlgeschlagen');
              } finally {
                setBulkDraftsBusy(false);
                setSelectedIds(new Set());
              }
            }}
          />

          {/* Liste */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Lade Forderungsakten...</div>
          ) : filtered.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Scale className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground">Keine Forderungsakten gefunden</p>
                <Button variant="outline" className="mt-4 gap-2" onClick={() => setShowNewForm(true)}>
                  <Plus className="w-5 h-5" /> Erste Forderungsakte anlegen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <ForderungsTable
              forderungen={filtered}
              crossLinks={crossLinks}
              visibility={columnVisibility}
              selectedIds={selectedIds}
              onToggleSelect={(id, checked) => {
                setSelectedIds(prev => {
                  const next = new Set(prev);
                  if (checked) next.add(id); else next.delete(id);
                  return next;
                });
              }}
              onSelect={f => setSelectedForderung(f)}
              onDelete={id => { deleteForderung(id); toast.success('Forderungsakte gelöscht'); }}
            />
          )}
        </TabsContent>

        {/* ═══ TAB: OFFENE POSTEN (Neu - Rentsoft) ═══ */}
        <TabsContent value="offene_posten" className="space-y-4">
          <OffenePostenView
            openItems={rentsoft.openItems}
            invoices={rentsoft.invoices}
            isLoading={rentsoft.isLoading}
            lastSync={rentsoft.lastSync}
            onRefresh={async () => {
              try {
                await Promise.all([rentsoft.fetchOpenItems(), rentsoft.fetchInvoices()]);
                toast.success('Rentsoft-Daten aktualisiert');
              } catch {
                toast.error('Fehler beim Laden der Rentsoft-Daten');
              }
            }}
            forderungen={forderungen}
          />
        </TabsContent>

        {/* ═══ TAB: TAGEBUCH (Modul 2) ═══ */}
        <TabsContent value="tagebuch" className="space-y-4">
          <TagebuchView forderungen={forderungen} getEintraege={getEintraege} addEintrag={addEintrag} />
        </TabsContent>

        {/* ═══ TAB: KI-PRÜFUNG (Modul 3) ═══ */}
        <TabsContent value="pruefung" className="space-y-4">
          <PruefAssistent forderungen={forderungen} updateForderung={updateForderung} />
        </TabsContent>

        {/* ═══ TAB: ANSCHREIBEN (Modul 4) ═══ */}
        <TabsContent value="anschreiben" className="space-y-4">
          <AnschreibenGenerator forderungen={forderungen} getEintraege={getEintraege} addDokument={addDokument} getDokumente={getDokumente} gmail={gmail} />
        </TabsContent>

        {/* ═══ TAB: MAHNWESEN (Modul 6) ═══ */}
        <TabsContent value="mahnwesen" className="space-y-4">
          <MahnwesenView
            forderungen={forderungen}
            getMahnungen={getMahnungen}
            addMahnung={addMahnung}
            updateForderung={updateForderung}
            gmail={gmail}
          />
        </TabsContent>
      </Tabs>

      {/* Neue Forderungsakte Dialog */}
      {showNewForm && (
        <NeueForderungDialog
          onClose={() => setShowNewForm(false)}
          onCreate={async (data) => {
            await createForderung(data);
            setShowNewForm(false);
            toast.success('Forderungsakte erstellt');
          }}
        />
      )}

      {/* Detail-Dialog */}
      {selectedForderung && (
        <ForderungDetailDialog
          forderung={selectedForderung}
          alleForderungen={forderungen}
          onClose={() => setSelectedForderung(null)}
          onOpenAkte={(id) => {
            const ziel = forderungen.find(f => f.id === id);
            if (ziel) setSelectedForderung(ziel);
          }}
          onUpdate={async (updates) => {
            await updateForderung(selectedForderung.id, updates);
            setSelectedForderung(null);
            toast.success('Forderungsakte aktualisiert');
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MODUL 5: DASHBOARD ÜBERSICHT (erweitert mit Rentsoft)
// ═══════════════════════════════════════════════════════════
function DashboardOverview({ stats, forderungen, rentsoftOpenItems, rentsoftFinancials, onSelect }: {
  stats: ReturnType<ReturnType<typeof useForderungen>['getStatistiken']>;
  forderungen: Forderungsakte[];
  rentsoftOpenItems: RentsoftOpenItem[];
  rentsoftFinancials: { openItemsCount: number; openItemsTotal: number; invoicesCount: number; monthlyRevenue: number };
  onSelect: (f: Forderungsakte) => void;
}) {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Offene Forderungen</p>
                <p className="text-3xl font-bold mt-1">{stats.gesamt_offen}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Gesamtbetrag offen</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(stats.gesamt_betrag)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <Euro className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rentsoft Offene Posten</p>
                <p className="text-3xl font-bold mt-1">{rentsoftFinancials.openItemsCount}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatCurrency(rentsoftFinancials.openItemsTotal)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Verjährung bald</p>
                <p className="text-3xl font-bold mt-1">{stats.verjährung_bald.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Nach Versicherung + Nach Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><PieChart className="w-5 h-5" /> Nach Versicherung</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.nach_versicherung.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Noch keine Daten</p>
            ) : (
              <div className="space-y-3">
                {stats.nach_versicherung.slice(0, 8).map(v => (
                  <div key={v.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{v.name}</span>
                      <Badge variant="secondary" className="text-xs">{v.anzahl}</Badge>
                    </div>
                    <span className="font-semibold text-sm">{formatCurrency(v.betrag)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Nach Status</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.nach_status.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Noch keine Daten</p>
            ) : (
              <div className="space-y-3">
                {stats.nach_status.map(s => {
                  const cfg = STATUS_CONFIG[s.status] || { label: s.status, color: 'bg-gray-100 text-gray-600' };
                  const pct = forderungen.length > 0 ? (s.anzahl / forderungen.length) * 100 : 0;
                  return (
                    <div key={s.status}>
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={cfg.color}>{cfg.label}</Badge>
                        <span className="text-sm font-medium">{s.anzahl}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary/60 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Verjährungswarnungen */}
      {stats.verjährung_bald.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" /> Verjährungswarnungen
            </CardTitle>
            <CardDescription>Forderungen deren Verjährungsfrist in den nächsten 3 Monaten abläuft</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.verjährung_bald.map(f => {
                const days = daysUntil(f.verjährungsfrist);
                return (
                  <div key={f.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors" onClick={() => onSelect(f)}>
                    <div>
                      <span className="font-medium">{f.akten_nr || 'Ohne Nr.'}</span>
                      <span className="text-muted-foreground mx-2">—</span>
                      <span>{f.versicherung_gegner || 'Unbekannt'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">{formatCurrency(f.gesamtbetrag)}</span>
                      <Badge variant="destructive">Noch {days} Tage</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// OFFENE POSTEN VIEW (Rentsoft Integration)
// ═══════════════════════════════════════════════════════════
function OffenePostenView({ openItems, invoices, isLoading, lastSync, onRefresh, forderungen }: {
  openItems: RentsoftOpenItem[];
  invoices: RentsoftInvoice[];
  isLoading: boolean;
  lastSync: string | null;
  onRefresh: () => Promise<void>;
  forderungen: Forderungsakte[];
}) {
  const [searchOP, setSearchOP] = useState('');
  const [viewMode, setViewMode] = useState<'open' | 'all'>('open');

  const filteredItems = useMemo(() => {
    const items = viewMode === 'open' ? openItems : invoices.map(inv => ({
      rechnungs_nr: inv.rechnungs_nr,
      datum: inv.datum,
      typ: inv.typ,
      status: 'Rechnung',
      empfaenger: inv.empfaenger,
      netto: inv.netto,
      netto_num: inv.netto_num,
    }));

    if (!searchOP) return items;
    const q = searchOP.toLowerCase();
    return items.filter(i =>
      i.rechnungs_nr?.toLowerCase().includes(q) ||
      i.empfaenger?.toLowerCase().includes(q)
    );
  }, [openItems, invoices, viewMode, searchOP]);

  const totalOpen = openItems.reduce((s, i) => s + i.netto_num, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-600" />
            Offene Posten — Rentsoft
          </h2>
          {lastSync && (
            <p className="text-xs text-muted-foreground mt-1">
              Letzter Sync: {new Date(lastSync).toLocaleString('de-DE')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading} className="gap-1.5">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Lade...' : 'Aktualisieren'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Offene Posten</p>
            <p className="text-2xl font-bold mt-1">{openItems.length}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Gesamtbetrag offen (Netto)</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(totalOpen)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Rechnungen gesamt</p>
            <p className="text-2xl font-bold mt-1">{invoices.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Suche nach Re-Nr, Empfänger..." className="pl-9"
            value={searchOP} onChange={e => setSearchOP(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button variant={viewMode === 'open' ? 'default' : 'outline'} size="sm"
            onClick={() => setViewMode('open')}>Offene Posten</Button>
          <Button variant={viewMode === 'all' ? 'default' : 'outline'} size="sm"
            onClick={() => setViewMode('all')}>Alle Rechnungen</Button>
        </div>
      </div>

      {/* Tabelle */}
      {isLoading && openItems.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Lade Rentsoft-Daten...</span>
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Receipt className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">
              {openItems.length === 0
                ? 'Keine Daten geladen. Klicke auf "Aktualisieren" um Rentsoft-Daten abzurufen.'
                : 'Keine Einträge gefunden'}
            </p>
            {openItems.length === 0 && (
              <Button variant="outline" className="mt-4 gap-2" onClick={onRefresh}>
                <RefreshCw className="w-4 h-4" /> Rentsoft-Daten laden
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Re.-Nr.</th>
                  <th className="text-left px-4 py-3 font-medium">Datum</th>
                  <th className="text-left px-4 py-3 font-medium">Typ</th>
                  <th className="text-left px-4 py-3 font-medium">Empfänger</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium">Betrag (Netto)</th>
                  <th className="text-center px-4 py-3 font-medium">Verknüpfung</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredItems.map((item, idx) => {
                  // Check if linked to a Forderungsakte
                  const linked = forderungen.find(f =>
                    f.rentsoft_rechnungs_nr === item.rechnungs_nr ||
                    f.mietvertrag_nr === item.rechnungs_nr
                  );
                  const isOverdue = item.datum && (() => {
                    const d = new Date(item.datum.split('.').reverse().join('-'));
                    return (Date.now() - d.getTime()) > 30 * 86400000;
                  })();

                  return (
                    <tr key={`${item.rechnungs_nr}-${idx}`} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{item.rechnungs_nr}</td>
                      <td className="px-4 py-3">{item.datum}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{item.typ}</Badge>
                      </td>
                      <td className="px-4 py-3">{item.empfaenger}</td>
                      <td className="px-4 py-3">
                        {viewMode === 'open' ? (
                          <Badge className={isOverdue ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                            {isOverdue ? 'Überfällig' : 'Offen'}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">{item.status}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {item.netto}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {linked ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" /> Verknüpft
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Nicht verknüpft
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td colSpan={5} className="px-4 py-3 font-bold text-right">Gesamt:</td>
                  <td className="px-4 py-3 text-right font-bold">
                    {formatCurrency(filteredItems.reduce((s, i) => s + i.netto_num, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// BULK-DRAFT-TOOLBAR (Issue #265)
// ═══════════════════════════════════════════════════════════
function BulkDraftToolbar({
  filtered, selectedIds, busy, onToggleAll, onBulkDraft,
}: {
  filtered: Forderungsakte[];
  selectedIds: Set<string>;
  busy: boolean;
  onToggleAll: (checked: boolean) => void;
  onBulkDraft: () => void | Promise<void>;
}) {
  const visibleCount = filtered.length;
  const selectedVisible = filtered.filter(f => selectedIds.has(f.id)).length;
  const allChecked = visibleCount > 0 && selectedVisible === visibleCount;
  const someChecked = selectedVisible > 0 && selectedVisible < visibleCount;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
      <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
        <Checkbox
          aria-label="Alle sichtbaren Forderungsakten auswählen"
          data-testid="bulk-select-all"
          checked={allChecked ? true : (someChecked ? 'indeterminate' : false)}
          onCheckedChange={(v) => onToggleAll(v === true)}
          disabled={visibleCount === 0}
        />
        <span>
          {selectedIds.size > 0
            ? `${selectedIds.size} ausgewählt`
            : 'Alle auswählen'}
        </span>
      </label>
      <Button
        onClick={onBulkDraft}
        disabled={selectedIds.size === 0 || busy}
        className="gap-2"
        data-testid="bulk-versicherung-drafts"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        Stellungnahme an Versicherung vorbereiten
        {selectedIds.size > 0 && <Badge variant="secondary" className="ml-1">{selectedIds.size}</Badge>}
      </Button>
    </div>
  );
}

// FORDERUNGS-TABELLE (Issue #263 + #265 Row-Select + #332 Cross-Link-Chips)
// ═══════════════════════════════════════════════════════════
function ForderungsTable({ forderungen, crossLinks, visibility, selectedIds, onToggleSelect, onSelect, onDelete }: {
  forderungen: Forderungsakte[];
  crossLinks: ForderungsCrossLinkMap;
  visibility: Record<ColumnId, boolean>;
  selectedIds: Set<string>;
  onToggleSelect: (id: string, checked: boolean) => void;
  onSelect: (f: Forderungsakte) => void;
  onDelete: (id: string) => void;
}) {
  const activeColumns = COLUMN_DEFS.filter(c => visibility[c.id]);
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" aria-label="Auswahl" />
            {activeColumns.map(col => (
              <TableHead
                key={col.id}
                data-column={col.id}
                data-testid={`forderungen-col-${col.id}`}
              >
                {col.label}
              </TableHead>
            ))}
            <TableHead className="w-10" aria-label="Aktionen" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {forderungen.map(f => (
            <TableRow
              key={f.id}
              data-testid={`forderungen-row-${f.id}`}
              data-state={selectedIds.has(f.id) ? 'selected' : undefined}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => onSelect(f)}
            >
              <TableCell className="align-top" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={`Forderungsakte ${f.akten_nr || f.id} auswählen`}
                  data-testid={`bulk-select-row-${f.id}`}
                  checked={selectedIds.has(f.id)}
                  onCheckedChange={(v) => onToggleSelect(f.id, v === true)}
                />
              </TableCell>
              {activeColumns.map(col => (
                <TableCell key={col.id} data-column={col.id} className="align-top">
                  <ForderungCellValue
                    forderung={f}
                    columnId={col.id}
                    crossLinks={crossLinks[f.id]}
                  />
                </TableCell>
              ))}
              <TableCell className="align-top">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Forderung löschen"
                  onClick={(e) => { e.stopPropagation(); onDelete(f.id); }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ForderungCellValue({ forderung: f, columnId, crossLinks }: {
  forderung: Forderungsakte;
  columnId: ColumnId;
  crossLinks?: ForderungsCrossLinkMap[string];
}) {
  switch (columnId) {
    case 'aktenNr':
      return <span className="font-semibold">{f.akten_nr || 'Ohne Nr.'}</span>;
    case 'status': {
      const cfg = STATUS_CONFIG[f.status] || STATUS_CONFIG.offen;
      return <Badge className={cfg.color}>{cfg.label}</Badge>;
    }
    case 'gegner':
      return <span>{f.unfallgegner_name || '—'}</span>;
    case 'versicherungGegner':
      return <span>{f.versicherung_gegner || '—'}</span>;
    case 'schadenNummer':
      return <span className="font-mono text-xs">{f.schaden_nummer || '—'}</span>;
    case 'abtretung': {
      // Best-effort bis #256 liefert. Feld `abtretung_status` kann null sein.
      if (!f.abtretung_status) return <span className="text-muted-foreground">—</span>;
      const labels: Record<string, string> = {
        ausstehend: 'offen',
        unterzeichnet: 'unterzeichnet',
        versendet: 'versendet',
      };
      return <span>{labels[f.abtretung_status] ?? f.abtretung_status}</span>;
    }
    case 'anwalt': {
      // Best-effort: Kein dediziertes Anwalt-Feld, Mahnstufe 'anwalt' als Proxy.
      const anwalt = f.mahnstufe === 'anwalt' || f.mahnstufe === 'gerichtlich';
      return anwalt ? <span>Ja</span> : <span className="text-muted-foreground">—</span>;
    }
    case 'unfallDatum':
      return <span>{formatDate(f.unfall_datum)}</span>;
    case 'verjaehrung': {
      if (!f.verjährungsfrist) return <span className="text-muted-foreground">—</span>;
      const tage = daysUntil(f.verjährungsfrist);
      const klasse = tage !== null && tage < 90 ? 'text-red-600 font-semibold' : '';
      return (
        <span className={klasse}>
          {formatDate(f.verjährungsfrist)}
          {tage !== null && <span className="text-xs ml-1">({tage} T.)</span>}
        </span>
      );
    }
    case 'offenerBetrag': {
      const mahnCfg = MAHNSTUFE_CONFIG[f.mahnstufe] || MAHNSTUFE_CONFIG.keine;
      return (
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{formatCurrency(f.gesamtbetrag)}</span>
          {f.mahnstufe !== 'keine' && <Badge className={`${mahnCfg.color} w-fit text-xs`}>{mahnCfg.label}</Badge>}
        </div>
      );
    }
    case 'plausibilitaet':
      // Platzhalter bis #258 die Plausibilitäts-Ampel liefert.
      return <span className="text-muted-foreground">—</span>;
    case 'crossLink':
      // Cross-Link-Chips (Issue #332): drei Icon-Chips (G/M/W), gefüllt wenn
      // verknüpft, hohl wenn nicht. Ersetzt den Platzhalter-Link aus #263.
      return <CrossLinkChips state={crossLinks ?? { G: null, M: null, W: null, demontage_auslage: false }} compact />;
    case 'letzteAktion':
      return <span className="text-muted-foreground">{formatDate(f.updated_at)}</span>;
    default:
      return <span className="text-muted-foreground">—</span>;
  }
}

// ═══════════════════════════════════════════════════════════
// VERKNÜPFUNGEN-FILTER (Issue #332)
// ═══════════════════════════════════════════════════════════
function VerknuepfungenFilterSelect({ value, onChange }: {
  value: VerknuepfungenFilter;
  onChange: (next: VerknuepfungenFilter) => void;
}) {
  // Encode the current filter as the same string we serialise into the URL,
  // so `alle` maps to an empty filter and everything else to exactly one state.
  const encoded = serializeVerknuepfungenFilter(value).split('=')[1] || 'alle';
  const handle = (v: string) => {
    switch (v) {
      case 'alle': onChange({}); return;
      case 'GMW': onChange({ hasAll: true }); return;
      case '-G': onChange({ missing: { G: true } }); return;
      case '-M': onChange({ missing: { M: true } }); return;
      case '-W': onChange({ missing: { W: true } }); return;
      case 'demontage': onChange({ demontage: true }); return;
    }
  };
  return (
    <Select value={encoded} onValueChange={handle}>
      <SelectTrigger className="w-full sm:w-60" data-testid="verknuepfungen-filter-trigger">
        <Link2 className="w-4 h-4 mr-2" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="alle">Alle Verknüpfungen</SelectItem>
        <SelectItem value="GMW">Nur mit allen 3 Verknüpfungen</SelectItem>
        <SelectItem value="-G">Ohne Gutachten</SelectItem>
        <SelectItem value="-M">Ohne Mietwagen</SelectItem>
        <SelectItem value="-W">Ohne Werkstatt</SelectItem>
        <SelectItem value="demontage">Mit Demontage (Werkstatt + Auslage)</SelectItem>
      </SelectContent>
    </Select>
  );
}


// ═══════════════════════════════════════════════════════════
// NEUE FORDERUNGSAKTE DIALOG (Modul 1)
// ═══════════════════════════════════════════════════════════
function NeueForderungDialog({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (data: Partial<Forderungsakte>) => Promise<void>;
}) {
  const [form, setForm] = useState<Partial<Forderungsakte>>({
    status: 'offen',
    mahnstufe: 'keine',
    klassengleichheit: true,
    totalschaden: false,
    eigenersparnis_prozent: 3.5,
    anspruchsgrundlage: '§ 249 BGB - Naturalrestitution',
  });
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onCreate(form);
    } catch (e: any) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto inset-0 sm:inset-auto w-full h-full sm:h-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-amber-600" />
            Neue Forderungsakte anlegen
          </DialogTitle>
        </DialogHeader>

        {/* Schritt-Anzeige */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map(s => (
            <button key={s} onClick={() => setStep(s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${step === s ? 'bg-primary text-primary-foreground' : step > s ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
              {step > s ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">{s}</span>}
              {s === 1 ? 'Unfallhergang' : s === 2 ? 'Mietdaten' : 'Reparatur'}
            </button>
          ))}
        </div>

        {/* Schritt 1: Unfallhergang */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Akten-Nr.</Label><Input placeholder="MW-2026-001" value={form.akten_nr || ''} onChange={e => set('akten_nr', e.target.value)} /></div>
              <div><Label>Unfalldatum</Label><Input type="date" value={form.unfall_datum || ''} onChange={e => set('unfall_datum', e.target.value)} /></div>
            </div>
            <div><Label>Unfallort</Label><Input placeholder="z.B. Hagen, Märkischer Ring / Ecke Körnerstraße" value={form.unfall_ort || ''} onChange={e => set('unfall_ort', e.target.value)} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Unfallgegner Name</Label><Input placeholder="Name des Unfallgegners" value={form.unfallgegner_name || ''} onChange={e => set('unfallgegner_name', e.target.value)} /></div>
              <div><Label>Kennzeichen Gegner</Label><Input placeholder="z.B. HA-XY 1234" value={form.unfallgegner_kennzeichen || ''} onChange={e => set('unfallgegner_kennzeichen', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Versicherung des Gegners</Label>
                <Select value={form.versicherung_gegner || ''} onValueChange={v => set('versicherung_gegner', v)}>
                  <SelectTrigger><SelectValue placeholder="Versicherung wählen" /></SelectTrigger>
                  <SelectContent>{VERSICHERUNGEN.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Schadennummer</Label><Input placeholder="Schadennummer der Versicherung" value={form.schaden_nummer || ''} onChange={e => set('schaden_nummer', e.target.value)} /></div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="gap-1">Weiter <ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        {/* Schritt 2: Mietdaten */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Mietvertrag-Nr.</Label><Input placeholder="LCS-2026-XXX" value={form.mietvertrag_nr || ''} onChange={e => set('mietvertrag_nr', e.target.value)} /></div>
              <div><Label>Tagessatz (EUR)</Label><Input type="number" step="0.01" placeholder="z.B. 89.00" value={form.tagessatz || ''} onChange={e => set('tagessatz', parseFloat(e.target.value) || 0)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Mietbeginn</Label><Input type="date" value={form.mietbeginn || ''} onChange={e => set('mietbeginn', e.target.value)} /></div>
              <div><Label>Mietende</Label><Input type="date" value={form.mietende || ''} onChange={e => set('mietende', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Fahrzeugklasse beschädigt</Label>
                <Select value={form.fahrzeugklasse_beschaedigt || ''} onValueChange={v => set('fahrzeugklasse_beschaedigt', v)}>
                  <SelectTrigger><SelectValue placeholder="Klasse wählen" /></SelectTrigger>
                  <SelectContent>{FAHRZEUGKLASSEN.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fahrzeugklasse gemietet</Label>
                <Select value={form.fahrzeugklasse_gemietet || ''} onValueChange={v => set('fahrzeugklasse_gemietet', v)}>
                  <SelectTrigger><SelectValue placeholder="Klasse wählen" /></SelectTrigger>
                  <SelectContent>{FAHRZEUGKLASSEN.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Switch checked={form.klassengleichheit || false} onCheckedChange={v => set('klassengleichheit', v)} />
              <Label>Klassengleichheit gegeben</Label>
              {!form.klassengleichheit && <Badge variant="destructive" className="ml-auto">Achtung: Höherstufung</Badge>}
            </div>
            <div><Label>Eigenersparnis (%)</Label><Input type="number" step="0.1" value={form.eigenersparnis_prozent || 3.5} onChange={e => set('eigenersparnis_prozent', parseFloat(e.target.value) || 3.5)} /></div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Zurück</Button>
              <Button onClick={() => setStep(3)} className="gap-1">Weiter <ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        {/* Schritt 3: Reparatur */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Reparaturdauer voraussichtlich (Tage)</Label><Input type="number" value={form.reparaturdauer_voraussichtlich || ''} onChange={e => set('reparaturdauer_voraussichtlich', parseInt(e.target.value) || 0)} /></div>
              <div><Label>Reparaturdauer tatsächlich (Tage)</Label><Input type="number" value={form.reparaturdauer_tatsaechlich || ''} onChange={e => set('reparaturdauer_tatsaechlich', parseInt(e.target.value) || 0)} /></div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Switch checked={form.totalschaden || false} onCheckedChange={v => set('totalschaden', v)} />
              <Label>Totalschaden</Label>
            </div>
            {form.totalschaden && (
              <div><Label>Wiederbeschaffungsdauer (Tage)</Label><Input type="number" value={form.wiederbeschaffungsdauer || ''} onChange={e => set('wiederbeschaffungsdauer', parseInt(e.target.value) || 0)} /></div>
            )}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800">Anspruchsgrundlage</p>
              <p className="text-sm text-blue-700 mt-1">§ 249 BGB — Naturalrestitution: Der Geschädigte hat Anspruch auf ein gleichwertiges Ersatzfahrzeug für die Dauer der Reparatur bzw. Wiederbeschaffung.</p>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Zurück</Button>
              <Button onClick={handleSave} disabled={saving} className="gap-1">
                {saving ? 'Speichern...' : 'Forderungsakte erstellen'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// DETAIL-DIALOG (v2 — Erweitert: Versicherung, Abtretung, Mietdauer, Rechnung)
// ═══════════════════════════════════════════════════════════
const ABTRETUNG_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  ausstehend: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  unterzeichnet: { label: 'Unterzeichnet', color: 'bg-green-100 text-green-800', icon: ClipboardCheck },
  versendet: { label: 'Versendet', color: 'bg-blue-100 text-blue-800', icon: Send },
};

function ForderungDetailDialog({ forderung: f, alleForderungen, onClose, onOpenAkte, onUpdate }: {
  forderung: Forderungsakte;
  alleForderungen: Forderungsakte[];
  onClose: () => void;
  onOpenAkte: (id: string) => void;
  onUpdate: (updates: Partial<Forderungsakte>) => Promise<void>;
}) {
  const dubletten = useMemo(() => findeDubletten(f, alleForderungen), [f, alleForderungen]);
  const [status, setStatus] = useState(f.status);
  const [mahnstufe, setMahnstufe] = useState(f.mahnstufe);
  const [abtretungStatus, setAbtretungStatus] = useState<string>(f.abtretung_status || 'ausstehend');
  const [detailTab, setDetailTab] = useState<'unfall' | 'versicherung' | 'mietdauer' | 'rechnung' | 'abtretung'>('unfall');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    versicherung_gegner: f.versicherung_gegner || '',
    schaden_nummer: f.schaden_nummer || '',
    versicherungsnummer: f.versicherungsnummer || '',
    versicherung_adresse: f.versicherung_adresse || '',
    versicherung_plz: f.versicherung_plz || '',
    versicherung_ort: f.versicherung_ort || '',
    versicherung_telefon: f.versicherung_telefon || '',
    versicherung_fax: f.versicherung_fax || '',
    versicherung_email: f.versicherung_email || '',
    rechnung_nr: f.rechnung_nr || '',
    rechnung_datum: f.rechnung_datum || '',
    rechnung_betrag: f.rechnung_betrag || 0,
  });

  // Mietdauer berechnen
  const mietdauer = (() => {
    if (f.mietdauer_tage) return f.mietdauer_tage;
    if (!f.mietbeginn || !f.mietende) return 0;
    return Math.ceil((new Date(f.mietende).getTime() - new Date(f.mietbeginn).getTime()) / 86400000);
  })();

  const eigenersparnis = (f.tagessatz || 0) * mietdauer * ((f.eigenersparnis_prozent || 3.5) / 100);
  const nettoGesamt = (f.tagessatz || 0) * mietdauer - eigenersparnis;

  const handleSave = () => {
    const updates: Partial<Forderungsakte> = {
      status,
      mahnstufe,
      abtretung_status: abtretungStatus as any,
    };
    if (isEditing) {
      Object.assign(updates, {
        versicherung_gegner: editForm.versicherung_gegner || null,
        schaden_nummer: editForm.schaden_nummer || null,
        versicherungsnummer: editForm.versicherungsnummer || null,
        versicherung_adresse: editForm.versicherung_adresse || null,
        versicherung_plz: editForm.versicherung_plz || null,
        versicherung_ort: editForm.versicherung_ort || null,
        versicherung_telefon: editForm.versicherung_telefon || null,
        versicherung_fax: editForm.versicherung_fax || null,
        versicherung_email: editForm.versicherung_email || null,
        rechnung_nr: editForm.rechnung_nr || null,
        rechnung_datum: editForm.rechnung_datum || null,
        rechnung_betrag: editForm.rechnung_betrag || null,
      });
    }
    onUpdate(updates);
  };

  const abtCfg = ABTRETUNG_CONFIG[abtretungStatus] || ABTRETUNG_CONFIG.ausstehend;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto inset-0 sm:inset-auto w-full h-full sm:h-auto sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> {f.akten_nr || 'Forderungsakte'}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_CONFIG[f.status]?.color || ''}>{STATUS_CONFIG[f.status]?.label || f.status}</Badge>
              {f.winvalue_ref && (
                <Badge variant="outline" className="text-xs gap-1">
                  <Link2 className="w-3 h-3" /> WV: {f.winvalue_ref}
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        {dubletten.length > 0 && (
          <DublettenWarnBanner treffer={dubletten} onOpen={onOpenAkte} />
        )}

        <PlausibilitaetsBlock forderung={f} />

        {/* Detail-Tabs */}
        <div className="flex gap-1 border-b overflow-x-auto">
          {[
            { id: 'unfall' as const, label: 'Unfall & Status', icon: AlertCircle },
            { id: 'versicherung' as const, label: 'Versicherung', icon: Shield },
            { id: 'mietdauer' as const, label: 'Mietdauer', icon: Calendar },
            { id: 'rechnung' as const, label: 'Rechnung', icon: Receipt },
            { id: 'abtretung' as const, label: 'Abtretung', icon: Pen },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setDetailTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                detailTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-[300px]">
          {/* ── TAB: UNFALL & STATUS ── */}
          {detailTab === 'unfall' && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Unfalldatum</span>
                  <p className="font-medium">{formatDate(f.unfall_datum)}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Unfallort</span>
                  <p className="font-medium">{f.unfall_ort || '\u2014'}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Unfallgegner</span>
                  <p className="font-medium">{f.unfallgegner_name || '\u2014'}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Kennzeichen Gegner</span>
                  <p className="font-medium">{f.unfallgegner_kennzeichen || '\u2014'}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Gesamtbetrag</span>
                  <p className="font-bold text-xl">{formatCurrency(f.gesamtbetrag)}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Mietvertrag-Nr.</span>
                  <p className="font-medium">{f.mietvertrag_nr || '\u2014'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <Label>Status</Label>
                  <Select value={status} onValueChange={(v: any) => setStatus(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mahnstufe</Label>
                  <Select value={mahnstufe} onValueChange={(v: any) => setMahnstufe(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(MAHNSTUFE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {f.risiko_einschaetzung && (
                <div className={`p-3 rounded-lg border ${RISIKO_CONFIG[f.risiko_einschaetzung]?.color || ''}`}>
                  <p className="font-medium">KI-Risikoeinsch\u00e4tzung: {RISIKO_CONFIG[f.risiko_einschaetzung]?.label}</p>
                </div>
              )}
            </div>
          )}

          {/* ── TAB: VERSICHERUNG ── */}
          {detailTab === 'versicherung' && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Zuständige Versicherung
                </h3>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(!isEditing)} className="gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> {isEditing ? 'Abbrechen' : 'Bearbeiten'}
                </Button>
              </div>

              {!isEditing ? (
                <Card>
                  <CardContent className="pt-4 pb-4">
                    {f.versicherung_gegner ? (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="font-bold text-lg">{f.versicherung_gegner}</p>
                            {f.versicherungsnummer && (
                              <p className="text-sm text-muted-foreground">VN: {f.versicherungsnummer}</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          {(f.versicherung_adresse || f.versicherung_plz || f.versicherung_ort) && (
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                              <div>
                                {f.versicherung_adresse && <p>{f.versicherung_adresse}</p>}
                                {(f.versicherung_plz || f.versicherung_ort) && (
                                  <p>{[f.versicherung_plz, f.versicherung_ort].filter(Boolean).join(' ')}</p>
                                )}
                              </div>
                            </div>
                          )}
                          {f.versicherung_telefon && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-muted-foreground" />
                              <a href={`tel:${f.versicherung_telefon}`} className="text-primary hover:underline">{f.versicherung_telefon}</a>
                            </div>
                          )}
                          {f.versicherung_fax && (
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span>Fax: {f.versicherung_fax}</span>
                            </div>
                          )}
                          {f.versicherung_email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <a href={`mailto:${f.versicherung_email}`} className="text-primary hover:underline">{f.versicherung_email}</a>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-4 pt-2 border-t text-sm">
                          <div>
                            <span className="text-muted-foreground">Schadennummer:</span>
                            <span className="font-medium ml-2">{f.schaden_nummer || '\u2014'}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Shield className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-muted-foreground">Keine Versicherung zugeordnet</p>
                        <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => setIsEditing(true)}>
                          <Plus className="w-4 h-4" /> Versicherung eintragen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-4 pb-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label>Versicherung</Label><Input value={editForm.versicherung_gegner} onChange={e => setEditForm(p => ({ ...p, versicherung_gegner: e.target.value }))} placeholder="z.B. HUK-COBURG" /></div>
                      <div><Label>Schadennummer</Label><Input value={editForm.schaden_nummer} onChange={e => setEditForm(p => ({ ...p, schaden_nummer: e.target.value }))} placeholder="z.B. S-2026-123456" /></div>
                      <div><Label>Versicherungsnummer</Label><Input value={editForm.versicherungsnummer} onChange={e => setEditForm(p => ({ ...p, versicherungsnummer: e.target.value }))} /></div>
                      <div><Label>Adresse</Label><Input value={editForm.versicherung_adresse} onChange={e => setEditForm(p => ({ ...p, versicherung_adresse: e.target.value }))} /></div>
                      <div><Label>PLZ</Label><Input value={editForm.versicherung_plz} onChange={e => setEditForm(p => ({ ...p, versicherung_plz: e.target.value }))} /></div>
                      <div><Label>Ort</Label><Input value={editForm.versicherung_ort} onChange={e => setEditForm(p => ({ ...p, versicherung_ort: e.target.value }))} /></div>
                      <div><Label>Telefon</Label><Input value={editForm.versicherung_telefon} onChange={e => setEditForm(p => ({ ...p, versicherung_telefon: e.target.value }))} /></div>
                      <div><Label>Fax</Label><Input value={editForm.versicherung_fax} onChange={e => setEditForm(p => ({ ...p, versicherung_fax: e.target.value }))} /></div>
                      <div className="sm:col-span-2"><Label>E-Mail</Label><Input value={editForm.versicherung_email} onChange={e => setEditForm(p => ({ ...p, versicherung_email: e.target.value }))} /></div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ── TAB: MIETDAUER ── */}
          {detailTab === 'mietdauer' && (
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Mietdauer & Berechnung
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border-emerald-200 bg-emerald-50/30">
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Mietdauer</p>
                    <p className="text-3xl font-bold mt-1">{mietdauer}</p>
                    <p className="text-sm text-muted-foreground">Tage</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Tagessatz</p>
                    <p className="text-2xl font-bold mt-1">{f.tagessatz ? formatCurrency(f.tagessatz) : '\u2014'}</p>
                    <p className="text-sm text-muted-foreground">netto / Tag</p>
                  </CardContent>
                </Card>
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Gesamtforderung</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(f.gesamtbetrag)}</p>
                    <p className="text-sm text-muted-foreground">brutto</p>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Mietbeginn</span>
                      <p className="font-medium">{formatDate(f.mietbeginn)}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Mietende</span>
                      <p className="font-medium">{formatDate(f.mietende)}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Fahrzeugklasse beschädigt</span>
                      <p className="font-medium">{f.fahrzeugklasse_beschaedigt || '\u2014'}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Fahrzeugklasse gemietet</span>
                      <p className="font-medium">{f.fahrzeugklasse_gemietet || '\u2014'}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Klassengleichheit</span>
                      <p>{f.klassengleichheit ? <Badge className="bg-green-100 text-green-800">Gegeben</Badge> : <Badge variant="destructive">Höherstufung</Badge>}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Eigenersparnis</span>
                      <p className="font-medium">{f.eigenersparnis_prozent}% = {formatCurrency(eigenersparnis)}</p>
                    </div>
                  </div>
                  {f.totalschaden && (
                    <div className="mt-3 p-2 bg-red-50 rounded border border-red-200 text-sm">
                      <span className="font-medium text-red-800">Totalschaden</span>
                      {f.wiederbeschaffungsdauer && (
                        <span className="text-red-700 ml-2">\u2014 Wiederbeschaffungsdauer: {f.wiederbeschaffungsdauer} Tage</span>
                      )}
                    </div>
                  )}
                  {(f.reparaturdauer_voraussichtlich || f.reparaturdauer_tatsaechlich) && (
                    <div className="mt-3 flex gap-6 text-sm">
                      {f.reparaturdauer_voraussichtlich && (
                        <div><span className="text-muted-foreground">Reparaturdauer (vorauss.):</span> <span className="font-medium ml-1">{f.reparaturdauer_voraussichtlich} Tage</span></div>
                      )}
                      {f.reparaturdauer_tatsaechlich && (
                        <div><span className="text-muted-foreground">Reparaturdauer (tats.):</span> <span className="font-medium ml-1">{f.reparaturdauer_tatsaechlich} Tage</span></div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── TAB: RECHNUNG ── */}
          {detailTab === 'rechnung' && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-amber-600" />
                  Rechnungsdaten
                </h3>
                {!isEditing && (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="gap-1.5">
                    <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                  </Button>
                )}
              </div>

              {!isEditing ? (
                <Card>
                  <CardContent className="pt-4 pb-4">
                    {(f.rechnung_nr || f.rentsoft_rechnungs_nr) ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Rechnungsnummer</span>
                          <p className="font-medium">{f.rechnung_nr || f.rentsoft_rechnungs_nr || '\u2014'}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Rechnungsdatum</span>
                          <p className="font-medium">{formatDate(f.rechnung_datum)}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Rechnungsbetrag</span>
                          <p className="font-bold text-lg">{f.rechnung_betrag ? formatCurrency(f.rechnung_betrag) : formatCurrency(f.gesamtbetrag)}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">Mietvertrag-Nr.</span>
                          <p className="font-medium">{f.mietvertrag_nr || '\u2014'}</p>
                        </div>
                        {f.rechnung_pdf_url && (
                          <div className="sm:col-span-2">
                            <a href={f.rechnung_pdf_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-primary hover:underline">
                              <Download className="w-4 h-4" /> Rechnung als PDF herunterladen
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Receipt className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-muted-foreground">Noch keine Rechnung zugeordnet</p>
                        <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => setIsEditing(true)}>
                          <Plus className="w-4 h-4" /> Rechnung eintragen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-4 pb-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label>Rechnungsnummer</Label><Input value={editForm.rechnung_nr} onChange={e => setEditForm(p => ({ ...p, rechnung_nr: e.target.value }))} placeholder="z.B. RE-2026-001" /></div>
                      <div><Label>Rechnungsdatum</Label><Input type="date" value={editForm.rechnung_datum} onChange={e => setEditForm(p => ({ ...p, rechnung_datum: e.target.value }))} /></div>
                      <div><Label>Rechnungsbetrag</Label><Input type="number" step="0.01" value={editForm.rechnung_betrag || ''} onChange={e => setEditForm(p => ({ ...p, rechnung_betrag: parseFloat(e.target.value) || 0 }))} /></div>
                    </div>
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>Abbrechen</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* ── TAB: ABTRETUNG ── */}
          {detailTab === 'abtretung' && (
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Pen className="w-5 h-5 text-purple-600" />
                Abtretungserklärung
              </h3>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Badge className={abtCfg.color + ' text-sm px-3 py-1'}>
                        {abtCfg.label}
                      </Badge>
                      {f.abtretung_datum && (
                        <span className="text-sm text-muted-foreground">vom {formatDate(f.abtretung_datum)}</span>
                      )}
                    </div>
                    <Select value={abtretungStatus} onValueChange={setAbtretungStatus}>
                      <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ABTRETUNG_CONFIG).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                    <p className="font-medium text-blue-800 mb-2">Abtretungserklärung gem. § 398 BGB</p>
                    <p className="text-blue-700">
                      Der Geschädigte <strong>{f.unfallgegner_name || '[Name]'}</strong> tritt hiermit seine Ansprüche
                      auf Erstattung der Mietwagenkosten aus dem Schadenfall vom {formatDate(f.unfall_datum)}
                      (Schadennummer: {f.schaden_nummer || '[Schadennummer]'}) gegenüber der
                      <strong> {f.versicherung_gegner || '[Versicherung]'}</strong> an die
                      LCS GmbH, Rahmedestrasse 65, 58507 Lüdenscheid, ab.
                    </p>
                    <p className="text-blue-700 mt-2">
                      Die Abtretung umfasst den Anspruch auf Erstattung der Mietwagenkosten in Höhe von
                      <strong> {formatCurrency(f.gesamtbetrag)}</strong> gemäß § 249 Abs. 2 BGB.
                    </p>
                  </div>

                  {f.abtretung_unterschrift_url && (
                    <div className="mt-4">
                      <p className="text-xs text-muted-foreground mb-2">Unterschrift des Geschädigten:</p>
                      <img src={f.abtretung_unterschrift_url} alt="Unterschrift" className="max-w-[200px] border rounded" />
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                    <p className="font-medium mb-1">Checkliste Abtretung:</p>
                    <ul className="space-y-1">
                      <li className="flex items-center gap-2">
                        {f.mietvertrag_nr ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Clock className="w-3.5 h-3.5 text-yellow-600" />}
                        Mietvertrag vorhanden {f.mietvertrag_nr ? `(${f.mietvertrag_nr})` : ''}
                      </li>
                      <li className="flex items-center gap-2">
                        {f.versicherung_gegner ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Clock className="w-3.5 h-3.5 text-yellow-600" />}
                        Versicherung zugeordnet {f.versicherung_gegner ? `(${f.versicherung_gegner})` : ''}
                      </li>
                      <li className="flex items-center gap-2">
                        {f.schaden_nummer ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Clock className="w-3.5 h-3.5 text-yellow-600" />}
                        Schadennummer vorhanden {f.schaden_nummer ? `(${f.schaden_nummer})` : ''}
                      </li>
                      <li className="flex items-center gap-2">
                        {abtretungStatus === 'unterzeichnet' || abtretungStatus === 'versendet' ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Clock className="w-3.5 h-3.5 text-yellow-600" />}
                        Abtretung unterzeichnet
                      </li>
                      <li className="flex items-center gap-2">
                        {abtretungStatus === 'versendet' ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Clock className="w-3.5 h-3.5 text-yellow-600" />}
                        An Versicherung versendet
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// MODUL 2: SCHADENSMINDERUNGSPFLICHT-TAGEBUCH
// ═══════════════════════════════════════════════════════════
function TagebuchView({ forderungen, getEintraege, addEintrag }: {
  forderungen: Forderungsakte[];
  getEintraege: (id: string) => Promise<SchadensminderungEintrag[]>;
  addEintrag: (e: Partial<SchadensminderungEintrag>) => Promise<any>;
}) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [eintraege, setEintraege] = useState<SchadensminderungEintrag[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newEntry, setNewEntry] = useState<Partial<SchadensminderungEintrag>>({
    datum: new Date().toISOString().split('T')[0],
    kategorie: 'reparaturdauer_ueberwacht',
    antwort: true,
  });

  useEffect(() => {
    if (selectedId) getEintraege(selectedId).then(setEintraege);
  }, [selectedId, getEintraege]);

  const handleAdd = async () => {
    if (!selectedId) return;
    try {
      await addEintrag({ ...newEntry, forderungsakte_id: selectedId });
      const updated = await getEintraege(selectedId);
      setEintraege(updated);
      setShowAdd(false);
      setNewEntry({ datum: new Date().toISOString().split('T')[0], kategorie: 'reparaturdauer_ueberwacht', antwort: true });
      toast.success('Eintrag hinzugefügt');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-full sm:w-80"><SelectValue placeholder="Forderungsakte wählen" /></SelectTrigger>
          <SelectContent>
            {forderungen.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.akten_nr || f.id.slice(0, 8)} — {f.versicherung_gegner || 'Unbekannt'}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedId && (
          <Button onClick={() => setShowAdd(true)} className="gap-1.5">
            <Plus className="w-4 h-4" /> Eintrag hinzufügen
          </Button>
        )}
      </div>

      {!selectedId ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Wähle eine Forderungsakte für das Schadensminderungspflicht-Tagebuch</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {eintraege.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">Noch keine Einträge vorhanden</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {eintraege.map(e => (
                <Card key={e.id}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{KATEGORIE_LABELS[e.kategorie] || e.kategorie}</Badge>
                          <span className="text-xs text-muted-foreground">{formatDate(e.datum)}</span>
                          {e.antwort !== null && (
                            e.antwort
                              ? <Badge className="bg-green-100 text-green-800 text-xs">Ja</Badge>
                              : <Badge className="bg-red-100 text-red-800 text-xs">Nein</Badge>
                          )}
                        </div>
                        {e.begruendung && <p className="text-sm">{e.begruendung}</p>}
                        {e.notiz && <p className="text-xs text-muted-foreground mt-1">{e.notiz}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Neuer Eintrag Dialog */}
          {showAdd && (
            <Dialog open onOpenChange={() => setShowAdd(false)}>
              <DialogContent>
                <DialogHeader><DialogTitle>Neuer Tagebuch-Eintrag</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>Datum</Label><Input type="date" value={newEntry.datum || ''} onChange={e => setNewEntry(p => ({ ...p, datum: e.target.value }))} /></div>
                  <div>
                    <Label>Kategorie</Label>
                    <Select value={newEntry.kategorie || ''} onValueChange={v => setNewEntry(p => ({ ...p, kategorie: v as any }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(KATEGORIE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={newEntry.antwort || false} onCheckedChange={v => setNewEntry(p => ({ ...p, antwort: v }))} />
                    <Label>Bestätigt / Durchgeführt</Label>
                  </div>
                  <div><Label>Begründung</Label><Textarea value={newEntry.begruendung || ''} onChange={e => setNewEntry(p => ({ ...p, begruendung: e.target.value }))} /></div>
                  <div><Label>Notiz</Label><Input value={newEntry.notiz || ''} onChange={e => setNewEntry(p => ({ ...p, notiz: e.target.value }))} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAdd(false)}>Abbrechen</Button>
                  <Button onClick={handleAdd}>Speichern</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MODUL 3: KI-PRÜFASSISTENT
// ═══════════════════════════════════════════════════════════
const PRUEF_FRAGEN = [
  { id: 'haftung_klar', frage: 'Ist die Haftungslage eindeutig geklärt? (100% Fremdverschulden)', kategorie: 'Haftung', gewicht: 3 },
  { id: 'klassengleich', frage: 'Wurde ein gleichwertiges oder günstigeres Fahrzeug angeboten?', kategorie: 'Fahrzeug', gewicht: 2 },
  { id: 'mietdauer_gerechtfertigt', frage: 'Ist die Mietdauer durch die Reparaturdauer gerechtfertigt?', kategorie: 'Dauer', gewicht: 3 },
  { id: 'totalschaden_wbd', frage: 'Liegt ein Totalschaden vor? Wenn ja: Wiederbeschaffungsdauer dokumentiert?', kategorie: 'Schaden', gewicht: 2 },
  { id: 'versicherung_informiert', frage: 'Wurde die Versicherung über den Mietbeginn informiert?', kategorie: 'Kommunikation', gewicht: 2 },
  { id: 'eigenersparnis', frage: 'Wurde die Eigenersparnis (3-5% bei Neuwagen) berücksichtigt?', kategorie: 'Berechnung', gewicht: 1 },
  { id: 'alternative_angeboten', frage: 'Wurde dem Kunden eine günstigere Alternative angeboten?', kategorie: 'Schadensminderung', gewicht: 2 },
  { id: 'reparatur_ueberwacht', frage: 'Wurde die Reparaturdauer aktiv überwacht?', kategorie: 'Schadensminderung', gewicht: 2 },
  { id: 'kunde_pflichten', frage: 'Wurde der Kunde über seine Schadensminderungspflicht informiert?', kategorie: 'Dokumentation', gewicht: 1 },
  { id: 'tagessatz_marktkonform', frage: 'Ist der Tagessatz marktkonform (Schwacke/Fraunhofer)?', kategorie: 'Berechnung', gewicht: 2 },
];

function PruefAssistent({ forderungen, updateForderung }: {
  forderungen: Forderungsakte[];
  updateForderung: (id: string, updates: Partial<Forderungsakte>) => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [antworten, setAntworten] = useState<Record<string, boolean | null>>({});
  const [notizen, setNotizen] = useState<Record<string, string>>({});

  const selected = forderungen.find(f => f.id === selectedId);

  useEffect(() => {
    if (selected?.pruefung_daten) {
      setAntworten(selected.pruefung_daten.antworten || {});
      setNotizen(selected.pruefung_daten.notizen || {});
    } else {
      setAntworten({});
      setNotizen({});
    }
  }, [selected]);

  const score = useMemo(() => {
    let total = 0, reached = 0;
    PRUEF_FRAGEN.forEach(f => {
      total += f.gewicht;
      if (antworten[f.id] === true) reached += f.gewicht;
    });
    return total > 0 ? (reached / total) * 100 : 0;
  }, [antworten]);

  const risiko: 'stark' | 'mittel' | 'gefaehrdet' = score >= 75 ? 'stark' : score >= 45 ? 'mittel' : 'gefaehrdet';

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      await updateForderung(selectedId, {
        risiko_einschaetzung: risiko,
        pruefung_abgeschlossen: Object.keys(antworten).length === PRUEF_FRAGEN.length,
        pruefung_daten: { antworten, notizen, score, risiko },
      });
      toast.success('Prüfung gespeichert');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-full sm:w-80"><SelectValue placeholder="Forderungsakte wählen" /></SelectTrigger>
          <SelectContent>
            {forderungen.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.akten_nr || f.id.slice(0, 8)} — {f.versicherung_gegner || 'Unbekannt'}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedId ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Brain className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Wähle eine Forderungsakte für die KI-gestützte Prüfung</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Risikoeinschätzung */}
          <Card className={`border ${RISIKO_CONFIG[risiko].color}`}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => { const Icon = RISIKO_CONFIG[risiko].icon; return <Icon className="w-6 h-6" />; })()}
                  <div>
                    <p className="font-bold text-lg">{RISIKO_CONFIG[risiko].label}</p>
                    <p className="text-sm opacity-80">Score: {Math.round(score)}% — {Object.keys(antworten).length}/{PRUEF_FRAGEN.length} Fragen beantwortet</p>
                  </div>
                </div>
                <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center font-bold text-xl"
                  style={{ borderColor: risiko === 'stark' ? '#16a34a' : risiko === 'mittel' ? '#d97706' : '#dc2626' }}>
                  {Math.round(score)}%
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fragen */}
          <div className="space-y-3">
            {PRUEF_FRAGEN.map(f => (
              <Card key={f.id} className={antworten[f.id] === true ? 'border-green-200 bg-green-50/30' : antworten[f.id] === false ? 'border-red-200 bg-red-50/30' : ''}>
                <CardContent className="py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">{f.kategorie}</Badge>
                        <span className="text-xs text-muted-foreground">Gewicht: {f.gewicht}</span>
                      </div>
                      <p className="text-sm font-medium">{f.frage}</p>
                      <Input placeholder="Notiz..." className="mt-2 text-xs h-8" value={notizen[f.id] || ''}
                        onChange={e => setNotizen(p => ({ ...p, [f.id]: e.target.value }))} />
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant={antworten[f.id] === true ? 'default' : 'outline'}
                        className={antworten[f.id] === true ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => setAntworten(p => ({ ...p, [f.id]: true }))}>
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant={antworten[f.id] === false ? 'default' : 'outline'}
                        className={antworten[f.id] === false ? 'bg-red-600 hover:bg-red-700' : ''}
                        onClick={() => setAntworten(p => ({ ...p, [f.id]: false }))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* BGH-Rechtsprechung */}
          <Card className="bg-blue-50/50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2 text-blue-800"><Gavel className="w-4 h-4" /> Relevante BGH-Rechtsprechung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-blue-900">
              <p><strong>BGH VI ZR 161/17:</strong> Der Geschädigte darf ein Fahrzeug der gleichen Klasse anmieten. Eine Pflicht zur Anmietung eines günstigeren Fahrzeugs besteht nur bei konkretem Nachweis durch den Schädiger.</p>
              <p><strong>BGH VI ZR 76/18:</strong> Die Mietdauer muss sich an der tatsächlichen Reparaturdauer orientieren. Bei Totalschaden: Wiederbeschaffungsdauer + angemessene Überlegungsfrist.</p>
              <p><strong>BGH VI ZR 274/17:</strong> Eigenersparnis von 3-5% ist bei Anmietung eines gleichwertigen Fahrzeugs abzuziehen.</p>
              <p><strong>BGH VI ZR 398/16:</strong> Der Geschädigte muss sich im Rahmen der Schadensminderungspflicht (§ 254 BGB) erkundigen, ob günstigere Angebote verfügbar sind.</p>
            </CardContent>
          </Card>

          <Button onClick={handleSave} className="w-full gap-2"><CheckCircle className="w-4 h-4" /> Prüfung speichern</Button>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MODUL 4: AUTOMATISCHES ANSCHREIBEN + E-MAIL-VERSAND
// ═══════════════════════════════════════════════════════════
function AnschreibenGenerator({ forderungen, getEintraege, addDokument, getDokumente, gmail }: {
  forderungen: Forderungsakte[];
  getEintraege: (id: string) => Promise<SchadensminderungEintrag[]>;
  addDokument: (d: any) => Promise<any>;
  getDokumente: (id: string) => Promise<any[]>;
  gmail: ReturnType<typeof useGmail>;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [dokumente, setDokumente] = useState<any[]>([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailNote, setEmailNote] = useState('');

  const selected = forderungen.find(f => f.id === selectedId);

  useEffect(() => {
    if (selectedId) getDokumente(selectedId).then(setDokumente);
  }, [selectedId, getDokumente]);

  const generateAnschreiben = async () => {
    if (!selected) return;
    setGenerating(true);

    const eintraege = await getEintraege(selected.id);
    const mietTage = selected.mietbeginn && selected.mietende
      ? Math.ceil((new Date(selected.mietende).getTime() - new Date(selected.mietbeginn).getTime()) / 86400000)
      : 0;

    const html = `
<div style="font-family: 'Times New Roman', serif; max-width: 700px; margin: 0 auto; padding: 40px; line-height: 1.6;">
  <div style="text-align: right; margin-bottom: 40px;">
    <strong>LCS GmbH</strong><br>
    Mietwagen &amp; Autoglas<br>
    Hagen<br><br>
    ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
  </div>

  <div style="margin-bottom: 30px;">
    <strong>${selected.versicherung_gegner || '[Versicherung]'}</strong><br>
    Schadenabteilung<br><br>
    <strong>Schadennummer: ${selected.schaden_nummer || '[Schadennummer]'}</strong><br>
    Unfalldatum: ${formatDate(selected.unfall_datum)}<br>
    Unfallgegner: ${selected.unfallgegner_name || '[Name]'}, Kfz-Kennzeichen: ${selected.unfallgegner_kennzeichen || '[Kennzeichen]'}
  </div>

  <h2 style="font-size: 16px; margin-bottom: 20px;">Geltendmachung des Mietwagenanspruchs gem. § 249 Abs. 2 BGB</h2>

  <p>Sehr geehrte Damen und Herren,</p>

  <p>namens und im Auftrag unseres Kunden machen wir hiermit den Anspruch auf Erstattung der Mietwagenkosten geltend, die infolge des Verkehrsunfalls vom ${formatDate(selected.unfall_datum)} in ${selected.unfall_ort || '[Ort]'} entstanden sind.</p>

  <h3 style="font-size: 14px; margin-top: 25px;">1. Anspruchsgrundlage</h3>
  <p>Der Geschädigte hat gem. § 249 Abs. 2 BGB Anspruch auf Naturalrestitution. Hierzu gehört die Erstattung der Kosten für ein gleichwertiges Ersatzfahrzeug für die Dauer der unfallbedingten Reparatur${selected.totalschaden ? ' bzw. Wiederbeschaffung' : ''}.</p>

  <h3 style="font-size: 14px; margin-top: 25px;">2. Mietdauer und Berechnung</h3>
  <p>
    Mietbeginn: ${formatDate(selected.mietbeginn)}<br>
    Mietende: ${formatDate(selected.mietende)}<br>
    Mietdauer: ${mietTage} Tage<br>
    Tagessatz: ${selected.tagessatz ? formatCurrency(selected.tagessatz) : '\u2014'}<br>
    ${selected.totalschaden ? `Wiederbeschaffungsdauer: ${selected.wiederbeschaffungsdauer || '\u2014'} Tage<br>` : `Reparaturdauer: ${selected.reparaturdauer_tatsaechlich || selected.reparaturdauer_voraussichtlich || '\u2014'} Tage<br>`}
    Eigenersparnis (${selected.eigenersparnis_prozent}%): ${selected.tagessatz ? formatCurrency(mietTage * selected.tagessatz * (selected.eigenersparnis_prozent / 100)) : '\u2014'}<br>
    <strong>Gesamtbetrag: ${formatCurrency(selected.gesamtbetrag)}</strong>
  </p>

  <h3 style="font-size: 14px; margin-top: 25px;">3. Fahrzeugklasse</h3>
  <p>
    Besch\u00e4digtes Fahrzeug: ${selected.fahrzeugklasse_beschaedigt || '\u2014'}<br>
    Gemietetes Fahrzeug: ${selected.fahrzeugklasse_gemietet || '\u2014'}<br>
    ${selected.klassengleichheit ? 'Klassengleichheit ist gegeben.' : '<strong style="color: red;">Hinweis: H\u00f6herstufung \u2014 Begr\u00fcndung liegt bei.</strong>'}
  </p>

  <h3 style="font-size: 14px; margin-top: 25px;">4. Nachweis der Schadensminderungspflicht</h3>
  <p>Wir haben die Schadensminderungspflicht gem. § 254 BGB vollumf\u00e4nglich beachtet. Im Einzelnen:</p>
  <ul>
    ${eintraege.filter(e => e.kategorie === 'alternative_angeboten' && e.antwort).map(e =>
      `<li>Am ${formatDate(e.datum)}: G\u00fcnstigere Alternative angeboten${e.begruendung ? ` \u2014 ${e.begruendung}` : ''}</li>`
    ).join('')}
    ${eintraege.filter(e => e.kategorie === 'reparaturdauer_ueberwacht' && e.antwort).map(e =>
      `<li>Am ${formatDate(e.datum)}: Reparaturdauer \u00fcberwacht${e.notiz ? ` \u2014 ${e.notiz}` : ''}</li>`
    ).join('')}
    ${eintraege.filter(e => e.kategorie === 'kunde_informiert' && e.antwort).map(e =>
      `<li>Am ${formatDate(e.datum)}: Kunde \u00fcber Pflichten informiert</li>`
    ).join('')}
    ${eintraege.filter(e => e.kategorie === 'versicherung_informiert' && e.antwort).map(e =>
      `<li>Am ${formatDate(e.datum)}: Versicherung informiert (${e.begruendung || 'schriftlich'})</li>`
    ).join('')}
    ${eintraege.length === 0 ? '<li><em>Noch keine Eintr\u00e4ge im Schadensminderungspflicht-Tagebuch</em></li>' : ''}
  </ul>

  <h3 style="font-size: 14px; margin-top: 25px;">5. Anlagen</h3>
  <ul>
    <li>Mietvertrag Nr. ${selected.mietvertrag_nr || '[Nr.]'}</li>
    <li>Reparaturrechnung / Gutachten</li>
    <li>Unfallbericht</li>
    <li>Schadensminderungspflicht-Protokoll</li>
  </ul>

  <p style="margin-top: 25px;">Wir bitten um Regulierung des Gesamtbetrags in H\u00f6he von <strong>${formatCurrency(selected.gesamtbetrag)}</strong> innerhalb von 14 Tagen auf das nachfolgende Konto.</p>

  <p style="margin-top: 30px;">
    Mit freundlichen Gr\u00fc\u00dfen<br><br>
    <strong>LCS GmbH</strong><br>
    Mietwagen &amp; Autoglas
  </p>
</div>`;

    setGeneratedHtml(html);
    setGenerating(false);
  };

  const saveAnschreiben = async () => {
    if (!selectedId || !generatedHtml) return;
    try {
      await addDokument({
        forderungsakte_id: selectedId,
        typ: 'anschreiben',
        titel: `Anschreiben an ${selected?.versicherung_gegner || 'Versicherung'} \u2014 ${formatDate(new Date().toISOString())}`,
        html_inhalt: generatedHtml,
      });
      const updated = await getDokumente(selectedId);
      setDokumente(updated);
      toast.success('Anschreiben gespeichert');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const openEmailDialog = () => {
    if (!selected) return;
    setEmailTo('');
    setEmailCc('');
    setEmailSubject(`Mietwagenanspruch gem. \u00a7 249 BGB \u2014 Schadennr. ${selected.schaden_nummer || ''} \u2014 ${selected.unfallgegner_name || ''}`);
    setEmailNote('');
    setShowEmailDialog(true);
  };

  const sendViaEmail = async () => {
    if (!emailTo.trim() || !generatedHtml) return;
    setSending(true);
    try {
      const fullBody = emailNote
        ? `<div style="font-family: Arial, sans-serif; margin-bottom: 30px; padding: 15px; background: #f8f9fa; border-radius: 8px;"><p>${emailNote.replace(/\n/g, '<br>')}</p></div>${generatedHtml}`
        : generatedHtml;

      await gmail.sendEmail(emailTo, emailSubject, fullBody, emailCc || undefined);

      // Auch als Dokument speichern
      await addDokument({
        forderungsakte_id: selectedId,
        typ: 'anschreiben',
        titel: `E-Mail an ${emailTo} \u2014 ${formatDate(new Date().toISOString())}`,
        html_inhalt: generatedHtml,
      });
      const updated = await getDokumente(selectedId);
      setDokumente(updated);

      setShowEmailDialog(false);
      toast.success(`E-Mail an ${emailTo} gesendet`);
    } catch (e: any) {
      toast.error(`Fehler beim Senden: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Gmail-Status */}
      {!gmail.isConnected && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-800">Gmail nicht verbunden \u2014 E-Mail-Versand nicht m\u00f6glich</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => gmail.connect()} className="gap-1.5">
              <Mail className="w-4 h-4" /> Gmail verbinden
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Select value={selectedId} onValueChange={v => { setSelectedId(v); setGeneratedHtml(''); }}>
          <SelectTrigger className="w-full sm:w-80"><SelectValue placeholder="Forderungsakte w\u00e4hlen" /></SelectTrigger>
          <SelectContent>
            {forderungen.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.akten_nr || f.id.slice(0, 8)} \u2014 {f.versicherung_gegner || 'Unbekannt'}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedId && (
          <Button onClick={generateAnschreiben} disabled={generating} className="gap-1.5">
            <FileText className="w-4 h-4" /> {generating ? 'Generiere...' : 'Anschreiben generieren'}
          </Button>
        )}
      </div>

      {!selectedId ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">W\u00e4hle eine Forderungsakte um ein Anschreiben zu generieren</p>
          </CardContent>
        </Card>
      ) : generatedHtml ? (
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">Vorschau Anschreiben</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={saveAnschreiben}>
                  <Download className="w-4 h-4" /> Speichern
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                  const w = window.open('', '_blank');
                  if (w) { w.document.write(generatedHtml); w.document.close(); w.print(); }
                }}>
                  <Send className="w-4 h-4" /> Drucken / PDF
                </Button>
                {gmail.isConnected && (
                  <Button size="sm" className="gap-1.5 bg-[#C41E3A] hover:bg-[#A3172E]" onClick={openEmailDialog}>
                    <Mail className="w-4 h-4" /> Per E-Mail senden
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-6 bg-white" dangerouslySetInnerHTML={{ __html: generatedHtml }} />
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Gespeicherte Dokumente */}
      {dokumente.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Gespeicherte Dokumente</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dokumente.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{d.titel}</span>
                    <Badge variant="outline" className="text-xs">{d.typ}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDate(d.created_at)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* E-Mail-Versand Dialog */}
      {showEmailDialog && (
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#C41E3A]" />
                Anschreiben per E-Mail senden
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                <Send className="w-4 h-4" />
                Von: {gmail.connectedEmail || 'Nicht verbunden'}
              </div>
              <div>
                <Label>An (Versicherung)</Label>
                <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="schaden@versicherung.de" />
              </div>
              <div>
                <Label>Cc (optional)</Label>
                <Input value={emailCc} onChange={e => setEmailCc(e.target.value)} placeholder="kopie@email.de" />
              </div>
              <div>
                <Label>Betreff</Label>
                <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
              </div>
              <div>
                <Label>Pers\u00f6nliche Notiz (optional, wird vor dem Anschreiben eingef\u00fcgt)</Label>
                <Textarea value={emailNote} onChange={e => setEmailNote(e.target.value)}
                  placeholder="z.B. Bitte um zeitnahe Regulierung..." rows={3} />
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Anhang: Anschreiben als HTML im E-Mail-Body</p>
                <p className="text-xs text-muted-foreground">Akte: {selected?.akten_nr || '\u2014'} | Versicherung: {selected?.versicherung_gegner || '\u2014'}</p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Abbrechen</Button>
              <Button onClick={sendViaEmail} disabled={sending || !emailTo.trim()} className="gap-1.5 bg-[#C41E3A] hover:bg-[#A3172E]">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sende...' : 'E-Mail senden'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MODUL 6: MAHNWESEN MIT ESKALATION
// ═══════════════════════════════════════════════════════════
const MAHNUNG_TEMPLATES: Record<string, { betreff: string; text: (f: Forderungsakte) => string }> = {
  '1_mahnung': {
    betreff: 'Zahlungserinnerung',
    text: (f) => `Sehr geehrte Damen und Herren,\n\nwir erlauben uns, Sie an die noch offene Forderung aus dem Verkehrsunfall vom ${formatDate(f.unfall_datum)} zu erinnern.\n\nSchadennummer: ${f.schaden_nummer || '[Schadennummer]'}\nGesamtbetrag: ${formatCurrency(f.gesamtbetrag)}\n\nWir bitten um Regulierung innerhalb von 14 Tagen.\n\nMit freundlichen Gr\u00fc\u00dfen\nLCS GmbH\nMietwagen & Autoglas`,
  },
  '2_mahnung': {
    betreff: '2. Mahnung',
    text: (f) => `Sehr geehrte Damen und Herren,\n\ntrotz unserer Zahlungserinnerung ist die Forderung aus dem Verkehrsunfall vom ${formatDate(f.unfall_datum)} weiterhin offen.\n\nSchadennummer: ${f.schaden_nummer || '[Schadennummer]'}\nGesamtbetrag: ${formatCurrency(f.gesamtbetrag)}\n\nWir fordern Sie hiermit letztmalig auf, den ausstehenden Betrag innerhalb von 7 Tagen zu begleichen. Andernfalls sehen wir uns gezwungen, die Forderung an ein Inkassounternehmen bzw. unseren Rechtsanwalt zu \u00fcbergeben.\n\nMit freundlichen Gr\u00fc\u00dfen\nLCS GmbH\nMietwagen & Autoglas`,
  },
  inkasso: {
    betreff: 'Letzte au\u00dfergericht\u00adliche Mahnung vor Inkasso\u00fcbergabe',
    text: (f) => `Sehr geehrte Damen und Herren,\n\ndie Forderung aus dem Verkehrsunfall vom ${formatDate(f.unfall_datum)} ist trotz mehrfacher Aufforderung weiterhin nicht beglichen.\n\nSchadennummer: ${f.schaden_nummer || '[Schadennummer]'}\nGesamtbetrag: ${formatCurrency(f.gesamtbetrag)}\n\nDies ist unsere letzte au\u00dfergericht\u00adliche Mahnung. Sollte der Betrag nicht innerhalb von 5 Werktagen auf unserem Konto eingehen, werden wir die Forderung ohne weitere Ank\u00fcndigung an ein Inkassounternehmen \u00fcbergeben.\n\nMit freundlichen Gr\u00fc\u00dfen\nLCS GmbH\nMietwagen & Autoglas`,
  },
};

function MahnwesenView({ forderungen, getMahnungen, addMahnung, updateForderung, gmail }: {
  forderungen: Forderungsakte[];
  getMahnungen: (id: string) => Promise<ForderungsMahnung[]>;
  addMahnung: (m: Partial<ForderungsMahnung>) => Promise<any>;
  updateForderung: (id: string, data: Partial<Forderungsakte>) => Promise<any>;
  gmail: ReturnType<typeof useGmail>;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [mahnungen, setMahnungen] = useState<ForderungsMahnung[]>([]);
  const [loadingMahnungen, setLoadingMahnungen] = useState(false);
  const [showNewMahnung, setShowNewMahnung] = useState(false);
  const [newStufe, setNewStufe] = useState<string>('1_mahnung');
  const [emailTo, setEmailTo] = useState('');
  const [emailText, setEmailText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [sending, setSending] = useState(false);

  const selected = forderungen.find(f => f.id === selectedId);

  // Forderungen die gemahnt werden k\u00f6nnen (offen, in_bearbeitung, strittig)
  const mahnbareForderungen = useMemo(() =>
    forderungen.filter(f => ['offen', 'in_bearbeitung', 'strittig'].includes(f.status)),
  [forderungen]);

  // F\u00e4llige Mahnungen (\u00e4lter als 14 Tage ohne Zahlung)
  const faelligeMahnungen = useMemo(() => {
    return mahnbareForderungen.filter(f => {
      if (!f.mietende) return false;
      const daysSinceEnd = Math.ceil((Date.now() - new Date(f.mietende).getTime()) / 86400000);
      return daysSinceEnd > 14;
    });
  }, [mahnbareForderungen]);

  useEffect(() => {
    if (selectedId) {
      setLoadingMahnungen(true);
      getMahnungen(selectedId).then(m => {
        setMahnungen(m);
        setLoadingMahnungen(false);
      }).catch(() => setLoadingMahnungen(false));
    }
  }, [selectedId, getMahnungen]);

  const prepareMahnung = (stufe: string) => {
    if (!selected) return;
    const template = MAHNUNG_TEMPLATES[stufe];
    if (!template) return;
    setNewStufe(stufe);
    setEmailSubject(`${template.betreff} \u2014 Schadennr. ${selected.schaden_nummer || ''} \u2014 ${selected.versicherung_gegner || ''}`);
    setEmailText(template.text(selected));
    setEmailTo('');
    setShowNewMahnung(true);
  };

  const sendMahnung = async () => {
    if (!selected || !emailTo.trim()) return;
    setSending(true);
    try {
      // E-Mail senden
      if (gmail.isConnected) {
        const htmlBody = emailText.replace(/\n/g, '<br>');
        await gmail.sendEmail(emailTo, emailSubject, htmlBody);
      }

      // Mahnung in DB speichern
      await addMahnung({
        forderungsakte_id: selectedId,
        mahnstufe: newStufe as any,
        gesendet_an: emailTo,
        betreff: emailSubject,
        inhalt: emailText,
        gesendet_am: new Date().toISOString(),
        status: gmail.isConnected ? 'gesendet' : 'erstellt',
      });

      // Mahnstufe auf Forderungsakte aktualisieren
      await updateForderung(selectedId, { mahnstufe: newStufe as any });

      // Mahnungen neu laden
      const updated = await getMahnungen(selectedId);
      setMahnungen(updated);

      setShowNewMahnung(false);
      toast.success(gmail.isConnected ? `Mahnung per E-Mail an ${emailTo} gesendet` : 'Mahnung gespeichert (nicht per E-Mail gesendet)');
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  // N\u00e4chste empfohlene Mahnstufe
  const getNextStufe = (): string => {
    if (!selected) return '1_mahnung';
    const current = selected.mahnstufe || 'keine';
    const order = ['keine', '1_mahnung', '2_mahnung', 'inkasso', 'anwalt', 'gerichtlich'];
    const idx = order.indexOf(current);
    return idx < order.length - 1 ? order[idx + 1] : current;
  };

  return (
    <div className="space-y-4">
      {/* Gmail-Status */}
      {!gmail.isConnected && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-800">Gmail nicht verbunden \u2014 Mahnungen k\u00f6nnen nur gespeichert, nicht gesendet werden</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => gmail.connect()} className="gap-1.5">
              <Mail className="w-4 h-4" /> Gmail verbinden
            </Button>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Mahnbare Forderungen</p>
            <p className="text-2xl font-bold mt-1">{mahnbareForderungen.length}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">F\u00e4llig (\u00fcber 14 Tage)</p>
            <p className="text-2xl font-bold mt-1 text-red-600">{faelligeMahnungen.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Offener Gesamtbetrag</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(mahnbareForderungen.reduce((s, f) => s + f.gesamtbetrag, 0))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Forderung ausw\u00e4hlen */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Select value={selectedId} onValueChange={v => { setSelectedId(v); setShowNewMahnung(false); }}>
          <SelectTrigger className="w-full sm:w-96"><SelectValue placeholder="Forderungsakte w\u00e4hlen" /></SelectTrigger>
          <SelectContent>
            {mahnbareForderungen.map(f => {
              const cfg = MAHNSTUFE_CONFIG[f.mahnstufe || 'keine'];
              return (
                <SelectItem key={f.id} value={f.id}>
                  {f.akten_nr || f.id.slice(0, 8)} \u2014 {f.versicherung_gegner || 'Unbekannt'} ({formatCurrency(f.gesamtbetrag)}) [{cfg?.label}]
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {!selectedId ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">W\u00e4hle eine Forderungsakte um das Mahnwesen zu verwalten</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Aktuelle Mahnstufe + Aktionen */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-600" />
                {selected?.akten_nr || 'Akte'} \u2014 {selected?.versicherung_gegner}
              </CardTitle>
              <CardDescription>
                Gesamtbetrag: {formatCurrency(selected?.gesamtbetrag || 0)} | Status: {STATUS_CONFIG[selected?.status || 'offen']?.label}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Aktuelle Mahnstufe:</span>
                <Badge className={MAHNSTUFE_CONFIG[selected?.mahnstufe || 'keine']?.color || 'bg-gray-100'}>
                  {MAHNSTUFE_CONFIG[selected?.mahnstufe || 'keine']?.label || 'Keine'}
                </Badge>
              </div>

              {/* Eskalations-Buttons */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(MAHNUNG_TEMPLATES).map(([stufe, tmpl]) => {
                  const isNext = stufe === getNextStufe();
                  return (
                    <Button key={stufe} variant={isNext ? 'default' : 'outline'} size="sm"
                      className={`gap-1.5 ${isNext ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                      onClick={() => prepareMahnung(stufe)}>
                      <Mail className="w-4 h-4" />
                      {tmpl.betreff}
                      {isNext && <Badge variant="secondary" className="ml-1 text-xs">Empfohlen</Badge>}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Neue Mahnung erstellen */}
          {showNewMahnung && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-amber-600" />
                  {MAHNSTUFE_CONFIG[newStufe]?.label || newStufe} erstellen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Empf\u00e4nger (E-Mail der Versicherung)</Label>
                  <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="schaden@versicherung.de" />
                </div>
                <div>
                  <Label>Betreff</Label>
                  <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
                </div>
                <div>
                  <Label>Mahntext</Label>
                  <Textarea value={emailText} onChange={e => setEmailText(e.target.value)} rows={10} className="font-mono text-sm" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={sendMahnung} disabled={sending || !emailTo.trim()} className="gap-1.5 bg-amber-600 hover:bg-amber-700">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {gmail.isConnected ? (sending ? 'Sende...' : 'Mahnung senden') : 'Mahnung speichern'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowNewMahnung(false)}>Abbrechen</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mahnhistorie */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" /> Mahnhistorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMahnungen ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Lade Mahnungen...</span>
                </div>
              ) : mahnungen.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-8">Noch keine Mahnungen f\u00fcr diese Akte</p>
              ) : (
                <div className="space-y-3">
                  {mahnungen.sort((a, b) => new Date(b.gesendet_am || b.created_at).getTime() - new Date(a.gesendet_am || a.created_at).getTime()).map(m => (
                    <div key={m.id} className="flex items-start justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge className={MAHNSTUFE_CONFIG[m.mahnstufe]?.color || 'bg-gray-100'}>
                            {MAHNSTUFE_CONFIG[m.mahnstufe]?.label || m.mahnstufe}
                          </Badge>
                          <Badge variant={m.status === 'gesendet' ? 'default' : 'outline'} className="text-xs">
                            {m.status === 'gesendet' ? 'Gesendet' : 'Erstellt'}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium">{m.betreff}</p>
                        <p className="text-xs text-muted-foreground">An: {m.gesendet_an}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(m.gesendet_am || m.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* F\u00e4llige Forderungen \u00dcbersicht */}
      {faelligeMahnungen.length > 0 && !selectedId && (
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" /> F\u00e4llige Forderungen (\u00fcber 14 Tage)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {faelligeMahnungen.map(f => {
                const days = f.mietende ? Math.ceil((Date.now() - new Date(f.mietende).getTime()) / 86400000) : 0;
                return (
                  <div key={f.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
                    onClick={() => setSelectedId(f.id)}>
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-medium">{f.akten_nr || 'Ohne Nr.'}</span>
                        <span className="text-muted-foreground mx-2">\u2014</span>
                        <span>{f.versicherung_gegner || 'Unbekannt'}</span>
                      </div>
                      <Badge className={MAHNSTUFE_CONFIG[f.mahnstufe || 'keine']?.color || 'bg-gray-100'}>
                        {MAHNSTUFE_CONFIG[f.mahnstufe || 'keine']?.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">{formatCurrency(f.gesamtbetrag)}</span>
                      <Badge variant="destructive">{days} Tage \u00fcberf\u00e4llig</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
